import React from "react";
import Card from "components/card";
import { MdClose, MdDownload } from "react-icons/md";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from "recharts";
import * as XLSX from 'xlsx';

const ViewRecord = ({ record, onClose }) => {
  // Calculate derived values
  const netWeight = record?.weighingBridgeWeight - (record?.numberOfBags || 0);
  const moistureDifference = (record?.entryMoisture || 0) - (record?.calibrationMoisture || 0);
  const totalMoistureLost = (record?.entryMoisture || 0) - (record?.moistureToAutoclave || 0);
  
  // Correct formula: Moisture Loss in RMW = Calibration Moisture - Moisture to Autoclave
  const moistureLossRMW = (record?.calibrationMoisture || 0) - (record?.moistureToAutoclave || 0);
  const finalWeightLoss = (record?.entryMoisture || 0) - totalMoistureLost;
  const finalWeightLossKg = netWeight * (finalWeightLoss / 100);

  const totalWeightLoss = (netWeight * (totalMoistureLost / 100)) + 
    (record?.dustOffloaded || 0) + (record?.dustAfterDrying || 0);
  const bagWeightKg = (record?.actualCalibratedBags || 0) * 3;
  const qtyToAutoclave = netWeight - totalWeightLoss - 
    (record?.dustOffloaded || 0) - (record?.dustAfterDrying || 0);
  const costOfCashew = (record?.pricePerKg || 0) * (record?.numberOfBags || 0);

  // Let's break down the calculation step by step with console.logs
  const moistureDiffReduction = netWeight * (moistureDifference / 100);
  const dustOffloaded = record?.dustOffloaded || 0;

  // QTY to be calibrated calculation
  const qtyToBeCalibrated = netWeight - moistureDiffReduction - dustOffloaded - totalWeightLoss;

  console.log({
    netWeight,
    moistureDifference,
    moistureDiffReduction,
    dustOffloaded,
    totalWeightLoss,
    qtyToBeCalibrated
  });

  // Sample data for the size distribution chart
  const sizeData = [
    { name: "Size 15", value: record?.size15 || 4 },
    { name: "Size 17", value: record?.size17 || 5 },
    { name: "Size 19", value: record?.size19 || 9 },
    { name: "Size 21", value: record?.size21 || 0 },
    { name: "Size 23", value: record?.size23 || 0 },
    { name: "Size 25", value: record?.size25 || 0 },
    { name: "Size 28", value: record?.size28 || 89 },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

  // Moisture Analysis Data for Chart
  const moistureData = [
    { name: "Entry Moisture", value: parseFloat(record?.entryMoisture) || 8.5 },
    { name: "Calibration Moisture", value: parseFloat(record?.calibrationMoisture) || 7.2 },
    { name: "Moisture to Autoclave", value: parseFloat(record?.moistureToAutoclave) || 6.8 },
    { name: "Moisture Loss RMW", value: parseFloat(moistureLossRMW) || 0.4 },
  ];

  // Group data into sections for organized display
  const sections = {
    "Basic Information": [
      { label: "Date", value: record?.date },
      { label: "Truck Number", value: record?.truckNumber },
      { label: "Origin", value: record?.origin },
    ],
    "Weight Measurements": [
      { label: "Weighing Bridge Weight", value: `${record?.weighingBridgeWeight} kg` },
      { label: "Net Weight", value: `${netWeight} kg` },
      { label: "Number of Bags", value: record?.numberOfBags },
      { label: "Bag Weight", value: `${bagWeightKg} kg` },
      { label: "Actual Calibrated Bags", value: record?.actualCalibratedBags },
      { label: "Actual Calibrated KG", value: `${record?.actualCalibratedKg} kg` },
    ],
    "Financial Information": [
      { label: "Price per Kg", value: `GHS ${record?.pricePerKg?.toFixed(2) || "0.00"}` },
      { label: "Cost of Cashew", value: `GHS ${costOfCashew.toFixed(2)}` },
    ],
    "Moisture Analysis": [
      { label: "Entry Moisture", value: `${record?.entryMoisture}%` },
      { label: "Calibration Moisture", value: `${record?.calibrationMoisture}%` },
      { label: "Moisture Difference", value: `${moistureDifference.toFixed(1)}%` },
      { label: "Moisture to Autoclave", value: `${record?.moistureToAutoclave}%` },
      { label: "Total Moisture Lost", value: `${totalMoistureLost.toFixed(1)}%` },
    ],
    "Quantity Information": [
      { label: "QTY to be Calibrated", value: qtyToBeCalibrated.toFixed(2) },
      { label: "QTY to Autoclave", value: qtyToAutoclave.toFixed(0) },
    ],
    "Weight Loss Analysis": [
      { label: "Total Weight Loss", value: `${totalWeightLoss.toFixed(0)} kg` },
      { label: "Final Weight Loss", value: `${finalWeightLoss.toFixed(2)}%` },
      { label: "Final Weight Loss KG", value: `${finalWeightLossKg.toFixed(2)} kg` },
      { label: "Moisture Loss in RMW", value: `${moistureLossRMW.toFixed(2)}%` },
    ],
    "Dust Information": [
      { label: "Dust Offloaded", value: `${record?.dustOffloaded} kg` },
      { label: "Dust After Drying", value: `${record?.dustAfterDrying} kg` },
    ],
  };

  const handleDownloadExcel = () => {
    // Prepare column headers and data row
    const excelData = [
      // Column Headers
      [
        'Date',
        'Truck Number',
        'Origin',
        'Weighing Bridge Weight (kg)',
        'Net Weight (kg)',
        'Number of Bags',
        'Bag Weight (kg)',
        'Actual Calibrated Bags',
        'Actual Calibrated KG',
        'Price per Kg (GHS)',
        'Cost of Cashew (GHS)',
        'Entry Moisture (%)',
        'Calibration Moisture (%)',
        'Moisture Difference (%)',
        'Moisture to Autoclave (%)',
        'Moisture Loss RMW (%)',
        'Total Moisture Lost (%)',
        'QTY to be Calibrated',
        'QTY to Autoclave',
        'Total Weight Loss (kg)',
        'Final Weight Loss (%)',
        'Final Weight Loss (kg)',
        'Dust Offloaded (kg)',
        'Dust After Drying (kg)'
      ],
      // Data Row
      [
        record?.date,
        record?.truckNumber,
        record?.origin,
        record?.weighingBridgeWeight,
        netWeight,
        record?.numberOfBags,
        bagWeightKg,
        record?.actualCalibratedBags,
        record?.actualCalibratedKg,
        record?.pricePerKg?.toFixed(2) || "0.00",
        costOfCashew.toFixed(2),
        record?.entryMoisture,
        record?.calibrationMoisture,
        moistureDifference.toFixed(1),
        record?.moistureToAutoclave,
        record?.moistureLossRMW || "0.8",
        totalMoistureLost.toFixed(1),
        qtyToBeCalibrated.toFixed(2),
        qtyToAutoclave.toFixed(0),
        totalWeightLoss.toFixed(0),
        finalWeightLoss.toFixed(2),
        finalWeightLossKg.toFixed(2),
        record?.dustOffloaded,
        record?.dustAfterDrying
      ]
    ];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Warehouse Record');

    // Generate filename with date
    const fileName = `warehouse_record_${record?.id || 'unknown'}_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Save file
    XLSX.writeFile(wb, fileName);
  };

  return (
    <>
      {/* Blurred Background Overlay */}
      <div 
        className="fixed inset-0 z-40 backdrop-blur-sm bg-black/30"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
        <div className="relative w-[95%] max-w-7xl rounded-xl bg-white p-6 shadow-xl dark:bg-navy-800">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
                RCN Warehouse Data
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                ID: {record?.id}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadExcel}
                className="rounded-full bg-lightPrimary p-2 text-brand-500 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                title="Download Excel"
              >
                <MdDownload className="h-6 w-6" />
              </button>
              <button
                onClick={onClose}
                className="rounded-full bg-lightPrimary p-2 text-brand-500 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
              >
                <MdClose className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="max-h-[80vh] overflow-y-auto">
            {/* Add Chart Section */}
            <Card extra="p-4 mb-6">
              <h3 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
                Moisture Analysis Chart
              </h3>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={moistureData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                      label={({name, value}) => `${name}: ${value}%`}
                    >
                      {moistureData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => `${value}%`}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {Object.entries(sections).map(([title, items]) => (
                <Card extra="p-4" key={title}>
                  <h3 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
                    {title}
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {items.map((item) => (
                      <div key={item.label}>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{item.label}</p>
                        <p className="text-base font-medium text-navy-700 dark:text-white">
                          {item.value || "N/A"}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewRecord; 